import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

// Embeddable cost-allocation panel (Phase 1 — FinOps IA).
// Extracted from pages/CostReport.jsx so the same allocation table can render
// both as the standalone /cost-report page AND inside the Monitoring "Cost
// Allocation" tab. Driven by a `clusterId` prop instead of ClusterContext so a
// host page can pass whichever cluster it is showing. Hits
// `/api/v2/clusters/{cluster_id}/allocations` (the REST allocation engine with
// idle/shared split + CSV/PDF export) — NOT the ClickHouse fast-path, which is
// the top-summary only.

const WINDOW_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const BUILTIN_AGGREGATES = [
  { value: 'namespace',          label: 'Namespace' },
  { value: 'controller',         label: 'Workload (controller)' },
  { value: 'node',               label: 'Node' },
  { value: 'label:team',         label: 'Label: team' },
  { value: 'label:env',          label: 'Label: env' },
  { value: 'label:cost-center',  label: 'Label: cost-center' },
];

const fmt = (v) =>
  typeof v === 'number'
    ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
    : '—';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Friendlier display name for row keys (strips cloud hostname suffixes, pretty-
// prints sentinel rows). Same logic as the standalone page.
const friendlyName = (raw) => {
  if (!raw) return '—';
  if (raw === '__unallocated__') return 'Unallocated (no label match)';
  if (raw === '__shared__') return 'Shared overhead';
  if (raw === '__idle__') return 'Idle (cluster total)';
  if (raw.startsWith('__idle__/')) {
    const node = raw.slice('__idle__/'.length);
    const short = node.replace(/\.(ap|us|eu|sa|af|me|ca)-[a-z0-9-]+\.compute\.internal$/, '');
    return `Idle on ${short}`;
  }
  return raw;
};

export default function CostAllocationPanel({ clusterId, embedded = false }) {
  const [windowSpec, setWindowSpec] = useState('7d');
  const [aggregate, setAggregate] = useState('namespace');
  const [shareIdle, setShareIdle] = useState(false);
  const [splitIdle, setSplitIdle] = useState('node');
  const [filterKey, setFilterKey] = useState('');
  const [filterVal, setFilterVal] = useState('');
  const [labelKeys, setLabelKeys] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Load label-keys so the group-by dropdown shows the cluster's real labels.
  useEffect(() => {
    if (!clusterId) return;
    let cancelled = false;
    axios
      .get(`${API}/v2/clusters/${clusterId}/allocations/label-keys?limit=20`, { headers: authHeaders() })
      .then((res) => {
        if (cancelled) return;
        const keys = (res.data?.label_keys || []).map((k) => k.key);
        setLabelKeys(keys);
      })
      .catch(() => {
        // Non-fatal — dropdown just won't show cluster-specific keys.
      });
    return () => { cancelled = true; };
  }, [clusterId]);

  const aggregateOptions = useMemo(() => {
    const dynamic = labelKeys
      .filter((k) => !BUILTIN_AGGREGATES.find((a) => a.value === `label:${k}`))
      .map((k) => ({ value: `label:${k}`, label: `Label: ${k}` }));
    return [...BUILTIN_AGGREGATES, ...dynamic];
  }, [labelKeys]);

  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    params.append('window', windowSpec);
    if (aggregate) params.append('aggregate', aggregate);
    if (filterKey && filterVal) params.append('filter', `${filterKey}=${filterVal}`);
    params.append('share_idle', String(shareIdle));
    params.append('split_idle', splitIdle);
    Object.entries(extra).forEach(([k, v]) => params.append(k, v));
    return params;
  }, [windowSpec, aggregate, filterKey, filterVal, shareIdle, splitIdle]);

  const loadRows = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get(
        `${API}/v2/clusters/${clusterId}/allocations?${buildParams().toString()}`,
        { headers: authHeaders() }
      );
      setRows(res.data?.rows || []);
      setTotalCost(res.data?.total_cost || 0);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'failed to load';
      setErrorMsg(msg);
      toast.error(`Failed to load cost allocation: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [clusterId, buildParams]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Idle vs allocated summary for the header bar.
  const idleTotal = useMemo(
    () => rows.reduce((s, r) => s + (r.cpu_cost_idle || 0) + (r.ram_cost_idle || 0), 0),
    [rows]
  );

  // Token-bearing fetch download (axios doesn't stream large CSV/PDF with custom headers).
  const download = (url, filename) => {
    const token = localStorage.getItem('token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) {
          const detail = (await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`;
          throw new Error(detail);
        }
        const blob = await r.blob();
        const dl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(dl);
      })
      .catch((e) => toast.error(`Download failed: ${e.message}`));
  };

  const downloadCSV = () =>
    download(
      `${API}/v2/clusters/${clusterId}/allocations?${buildParams({ format: 'csv' }).toString()}`,
      `allocations_${clusterId}_${windowSpec}.csv`
    );

  const downloadPDF = () =>
    download(
      `${API}/v2/clusters/${clusterId}/allocations/report.pdf?${buildParams().toString()}`,
      `optops_report_${clusterId}_${windowSpec}.pdf`
    );

  if (!clusterId) {
    return (
      <div className="text-center text-slate-400 py-10 text-sm">Select a cluster to view cost allocation.</div>
    );
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cost Allocation</h2>
            <p className="text-slate-500 text-sm mt-1">
              Group costs by namespace, workload, node, or any team/env/cost-center label. Showback today;
              immutable chargeback snapshots are auto-written nightly.
            </p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Window</label>
          <select className="w-full border rounded-md px-2 py-1 text-sm bg-white dark:bg-slate-900"
            value={windowSpec} onChange={(e) => setWindowSpec(e.target.value)}>
            {WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Group by</label>
          <select className="w-full border rounded-md px-2 py-1 text-sm bg-white dark:bg-slate-900"
            value={aggregate} onChange={(e) => setAggregate(e.target.value)}>
            <option value="">(no grouping — per pod)</option>
            {aggregateOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Filter dim</label>
          <input className="w-full border rounded-md px-2 py-1 text-sm bg-white dark:bg-slate-900"
            placeholder="e.g. namespace or label:env" value={filterKey}
            onChange={(e) => setFilterKey(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Filter value</label>
          <input className="w-full border rounded-md px-2 py-1 text-sm bg-white dark:bg-slate-900"
            placeholder="e.g. prod" value={filterVal}
            onChange={(e) => setFilterVal(e.target.value)} />
        </div>
        <div className="flex flex-col gap-2 justify-end">
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={shareIdle} onChange={(e) => setShareIdle(e.target.checked)} />
            Share idle into rows
          </label>
          <select className="w-full border rounded-md px-2 py-1 text-sm bg-white dark:bg-slate-900"
            value={splitIdle} disabled={shareIdle} onChange={(e) => setSplitIdle(e.target.value)}>
            <option value="node">Idle: per-node row</option>
            <option value="cluster">Idle: cluster-collapsed</option>
            <option value="none">Idle: hide</option>
          </select>
        </div>
      </div>

      {/* Summary + export */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center text-sm mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-slate-500">Total: </span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">{fmt(totalCost)}</span>
            </div>
            <div>
              <span className="text-slate-500">Idle: </span>
              <span className="font-semibold text-amber-600 dark:text-amber-400">{fmt(idleTotal)}</span>
              {totalCost > 0 && (
                <span className="text-slate-400 ml-1">({((idleTotal / totalCost) * 100).toFixed(0)}%)</span>
              )}
            </div>
            <span className="text-slate-400">{rows.length} rows</span>
          </div>
          <div className="flex gap-2">
            <button onClick={loadRows} disabled={loading}
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-700">
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button onClick={downloadCSV} disabled={!rows.length || loading}
              className="px-3 py-1.5 rounded-md bg-slate-700 text-white text-sm hover:bg-slate-600 disabled:opacity-50">
              CSV
            </button>
            <button onClick={downloadPDF} disabled={!rows.length || loading}
              className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-50">
              PDF
            </button>
          </div>
        </div>
        {errorMsg && <div className="text-sm text-red-600 mb-3">{errorMsg}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-2 py-2">Name</th>
                <th className="text-left px-2 py-2">Namespace</th>
                <th className="text-left px-2 py-2">Controller</th>
                <th className="text-right px-2 py-2">CPU $</th>
                <th className="text-right px-2 py-2">RAM $</th>
                <th className="text-right px-2 py-2">Idle $</th>
                <th className="text-right px-2 py-2">Shared $</th>
                <th className="text-right px-2 py-2 font-semibold">Total $</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isIdle = (r.name || '').startsWith('__idle__');
                const isUnalloc = r.name === '__unallocated__';
                return (
                  <tr key={r.name + i}
                    className={`border-t border-slate-100 dark:border-slate-700 ${isIdle ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}`}>
                    <td className="px-2 py-2 font-mono text-xs" title={r.name}>
                      {friendlyName(r.name)}
                      {isIdle && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">
                          unused capacity
                        </span>
                      )}
                      {isUnalloc && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded">
                          no team label
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{r.namespace || ''}</td>
                    <td className="px-2 py-2 text-slate-600 dark:text-slate-300">
                      {r.controller_kind ? `${r.controller_kind}:${r.controller}` : r.controller || ''}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmt(r.cpu_cost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmt(r.ram_cost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmt((r.cpu_cost_idle || 0) + (r.ram_cost_idle || 0))}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmt(r.shared_cost)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">{fmt(r.total_cost)}</td>
                  </tr>
                );
              })}
              {!rows.length && !loading && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-400 py-6">
                    No data — try a wider window, or ensure pods have labels.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
